import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { InvoiceInfo, OngoingOrderData, Server } from '../types/order';
import useData, { UpdateDataFunction } from '../hooks/utility/useData';
import { BaseComponentProps } from '../types';
import {
  CouponType,
  DeliveryMode,
  DropoffAction,
  OrderMethod,
  OrderPaymentType,
  OrderScheduleOption,
  PaymentMethod,
} from '../types/constants';
import { CreateOrderData, draftOrder } from '../services/api/OrderService';
import useStore from '../hooks/context/useStore';
import AddressFormatter from '../services/utils/address/AddressFormatter';
import { debounce } from 'lodash';
import { CustomerInfo } from '../types/customer';
import useOrderPreparationTime from '../hooks/order/useOrderPreparationTime';
import { OrderHelpers } from '../services/utils/order/OrderHelpers';
import { IEmptyCoupon } from '../types/coupon';
import useCustomerDetails from '../hooks/context/useCustomerDetails';

export type OngoingOrderContextType = {
  orderData: OngoingOrderData;
  orderDraft: Server.OrderData;
  isDelivery: boolean;
  isExternalDelivery: boolean;
  scheduledTime: null | string;
  timeLabel: string;
  loadingDraft: boolean;
  setOrderData: (data: OngoingOrderData) => void;
  updateOrderData: UpdateDataFunction<OngoingOrderData>;
  resetOrderData: () => void;
  prepareOrderData: (extra?: Partial<CreateOrderData>) => CreateOrderData;
  resetCustomerInfo: () => void;
  loadDraft: (extra?: Partial<CreateOrderData>) => Promise<Server.OrderData>;
};

export const OngoingOrderContext = React.createContext<OngoingOrderContextType>(null);

const defaultCustomerInfo: CustomerInfo = {
  phone_number: null,
  email: null,
  first_name: null,
  last_name: null,
};

const defaultInvoiceInfo: InvoiceInfo = {
  company_name: null,
  tax_id: null,
};

const defaultCouponInfo: IEmptyCoupon = {
  coupon_type: null,
  coupon_details: null,
};

export default function OngoingOrderProvider({ children }: BaseComponentProps) {
  const { storeData } = useStore();
  const { setCustomerData } = useCustomerDetails();

  const { getOrderMethodPrepTimeLabel } = useOrderPreparationTime();

  const { data, setData, updateData, resetData } = useData<OngoingOrderData>({
    call_id: null,
    method: null,
    payment_type: null,
    tip: null,
    customer: defaultCustomerInfo,
    schedule_info: {
      option: null,
      scheduled_for: null,
    },
    delivery_info: {
      street_name: null,
      street_number: null,
      city: null,
      state: null,
      country: null,
      zip_code: null,
      lat: null,
      lng: null,

      note: null,
      doorbell_name: null,

      zone: null,
      quote: null,
    },
    invoice_info: defaultInvoiceInfo,
    line_items: [],

    ...defaultCouponInfo,
  });

  const [draft, setDraft] = useState(null);
  const [loadingDraft, setLoadingDraft] = useState(false);

  const isDelivery = useMemo(() => data.method === OrderMethod.Delivery, [data.method]);

  const isExternalDelivery = useMemo(
    () =>
      storeData && isDelivery && storeData.order_settings.delivery_mode !== DeliveryMode.Internal,
    [isDelivery, storeData.order_settings.delivery_mode],
  );

  const scheduledTime = useMemo(
    () =>
      data.schedule_info.option === OrderScheduleOption.Scheduled
        ? data.schedule_info.scheduled_for
        : null,
    [data.schedule_info],
  );

  const timeLabel = useMemo(() => {
    if (data.method === OrderMethod.Delivery && data.delivery_info.quote) {
      return OrderHelpers.formatPickupTime(data.delivery_info.quote.dropoff_estimate);
    }

    if (!scheduledTime) {
      return `in ${getOrderMethodPrepTimeLabel(data.method ?? OrderMethod.Pickup)}`;
    }

    return OrderHelpers.formatPickupTime(scheduledTime);
  }, [data.method, scheduledTime]);

  const prepareOrderData = useCallback(
    (extra?: Partial<CreateOrderData>): CreateOrderData => ({
      method: data.method ?? OrderMethod.Pickup,
      scheduled_for: scheduledTime,

      customer_phone_number: data.customer.phone_number,
      customer_email: data.customer.email,
      customer_first_name: data.customer.first_name,
      customer_last_name: data.customer.last_name,

      delivery_quote_id: isExternalDelivery ? data.delivery_info.quote?.id : null,
      delivery_dropoff_action: isExternalDelivery ? DropoffAction.RingDoorBell : null,
      delivery_street_name: isDelivery ? data.delivery_info.street_name : null,
      delivery_street_number: isDelivery ? data.delivery_info.street_number : null,
      delivery_city: isDelivery ? data.delivery_info.city : null,
      delivery_state: isDelivery ? data.delivery_info.state : null,
      delivery_country: isDelivery ? data.delivery_info.country : null,
      delivery_zip_code: isDelivery ? data.delivery_info.zip_code : null,
      delivery_lat: isDelivery ? data.delivery_info.lat : null,
      delivery_lng: isDelivery ? data.delivery_info.lng : null,
      delivery_company_name: data.invoice_info.company_name,
      delivery_doorbell_name: null,
      customer_note: null,

      is_receipt_required: data.payment_type === OrderPaymentType.InStore,
      invoice_address: isDelivery
        ? AddressFormatter.format({
            street_name: data.delivery_info.street_name,
            street_number: data.delivery_info.street_number,
            country: data.delivery_info.country ?? storeData.location.country,
          })
        : null,
      invoice_city: isDelivery ? data.delivery_info.city : null,
      invoice_state: isDelivery ? data.delivery_info.state : null,
      invoice_zip_code: isDelivery ? data.delivery_info.zip_code : null,
      invoice_first_name: data.customer.first_name,
      invoice_last_name: data.customer.last_name,
      invoice_email: data.customer.email,
      invoice_company_name: data.invoice_info.company_name,
      invoice_tax_id: data.invoice_info.tax_id,

      payment_type: data.payment_type,
      payment_method_id: PaymentMethod.Cash,
      tip: data.tip,

      promo: data.coupon_type === CouponType.PromoCode ? data.coupon_details.code : null,

      line_items: data.line_items,

      ...(extra ?? {}),
    }),
    [data, storeData.location.country, isDelivery, isExternalDelivery],
  );

  const loadDraft = async (extra?: Partial<CreateOrderData>): Promise<Server.OrderData> => {
    setLoadingDraft(true);

    return draftOrder(storeData.id, prepareOrderData(extra))
      .then(({ data: _data }) => {
        setDraft(_data);

        return _data;
      })
      .finally(() => setTimeout(() => setLoadingDraft(false), 600));
  };

  const debouncedDraft = debounce(async (data?: CreateOrderData) => loadDraft(data), 250);

  useEffect(() => {
    storeData.id && debouncedDraft();

    return () => debouncedDraft.cancel();
  }, [
    storeData.id,
    data.method,
    data.tip,
    data.line_items,
    data.delivery_info.lat,
    data.delivery_info.lng,
    data.delivery_info.quote?.id,
    data.coupon_type,
  ]);

  const resetOrderData = () => {
    setCustomerData(null);
    resetData();
  };

  const resetCustomerInfo = () => {
    setCustomerData(null);
    updateData('customer', defaultCustomerInfo);
    updateData('invoice_info', defaultInvoiceInfo);
  };

  const context = useMemo(
    () => ({
      orderData: data,
      orderDraft: draft,
      isDelivery,
      isExternalDelivery,
      scheduledTime,
      timeLabel,
      loadingDraft,
      setOrderData: setData,
      updateOrderData: updateData,
      resetOrderData,
      prepareOrderData,
      resetCustomerInfo,
      loadDraft,
    }),
    [data, isDelivery, isExternalDelivery, scheduledTime, timeLabel, loadingDraft],
  );

  return <OngoingOrderContext.Provider value={context}>{children}</OngoingOrderContext.Provider>;
}
